// Sample blog data for BTW site
import { collection, addDoc } from 'firebase/firestore';
import { db } from './firebase';

const sampleBlogs = [
  {
    title: "The Art of Minimalist Interior Design",
    summary: "Explore how less can truly be more when it comes to creating serene, functional spaces that breathe with intention and purpose.",
    content: `
      <h2>Embracing Simplicity</h2>
      <p>Minimalist interior design isn't just about having fewer things—it's about choosing the right things. Every piece in a minimalist space serves a purpose, whether functional or aesthetic, and contributes to an overall sense of calm and clarity.</p>
      
      <h3>Key Principles of Minimalist Design</h3>
      <ul>
        <li>Quality over quantity in furniture selection</li>
        <li>Neutral color palettes that promote tranquility</li>
        <li>Clean lines and uncluttered surfaces</li>
        <li>Strategic use of natural light</li>
        <li>Thoughtful storage solutions that maintain visual calm</li>
      </ul>
      
      <blockquote>
        "The ability to simplify means to eliminate the unnecessary so that the necessary may speak." - Hans Hofmann
      </blockquote>
      
      <h3>Creating Visual Harmony</h3>
      <p>In our recent projects, we've discovered that the most successful minimalist spaces are those that balance emptiness with warmth. This means incorporating natural textures like wood and stone, adding plants for life and color, and ensuring that the few decorative elements present are meaningful to the inhabitants.</p>
      
      <p>The result is a space that feels both serene and inviting—a true sanctuary from the chaos of modern life.</p>
    `,
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80",
    category: "Design Philosophy",
    author: "Sarah Mitchell",
    date: "2025-01-15",
    readTime: "5 min read",
    tags: ["minimalism", "interior design", "philosophy", "simplicity"]
  },
  {
    title: "Sustainable Materials in Modern Architecture",
    summary: "How we're incorporating eco-friendly materials and practices into contemporary design without compromising on aesthetics or functionality.",
    content: `
      <h2>Building for the Future</h2>
      <p>Sustainability in architecture is no longer just a trend—it's a responsibility. As designers, we have the power to create spaces that not only serve their inhabitants but also respect and protect our environment.</p>
      
      <h3>Our Favorite Sustainable Materials</h3>
      <ul>
        <li><strong>Reclaimed Wood:</strong> Adds character while reducing waste</li>
        <li><strong>Bamboo:</strong> Fast-growing and incredibly versatile</li>
        <li><strong>Cork Flooring:</strong> Renewable and naturally antimicrobial</li>
        <li><strong>Recycled Steel:</strong> Durable and reduces mining impact</li>
        <li><strong>Low-VOC Paints:</strong> Healthier indoor air quality</li>
      </ul>
      
      <h3>The PHIBRIDGE Project Case Study</h3>
      <p>In our recent PHIBRIDGE residential project, we implemented a comprehensive sustainability strategy. From solar panels integrated seamlessly into the roof design to rainwater harvesting systems hidden within the landscape architecture, every element was considered for its environmental impact.</p>
      
      <blockquote>
        "We don't inherit the earth from our ancestors; we borrow it from our children." - Native American Proverb
      </blockquote>
      
      <p>The result was a home that reduces energy consumption by 40% while maintaining the luxurious aesthetic our clients expected. Sustainability and style can—and should—go hand in hand.</p>
    `,
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
    category: "Sustainability",
    author: "David Chen",
    date: "2025-01-10",
    readTime: "7 min read",
    tags: ["sustainability", "materials", "eco-friendly", "architecture"]
  },
  {
    title: "Commercial Spaces That Inspire Productivity",
    summary: "Behind the scenes of our STRATJURIS LAW PARTNERS project and how thoughtful design can boost workplace performance and employee satisfaction.",
    content: `
      <h2>Designing for Success</h2>
      <p>The modern workplace is evolving rapidly, and design plays a crucial role in this transformation. Our approach to the STRATJURIS LAW PARTNERS office was to create an environment that not only reflects the firm's professional identity but also enhances productivity and well-being.</p>
      
      <h3>Key Design Strategies</h3>
      <ul>
        <li>Biophilic design elements to reduce stress</li>
        <li>Flexible spaces that adapt to different work styles</li>
        <li>Acoustic solutions for focus and privacy</li>
        <li>Natural light optimization throughout the day</li>
        <li>Collaborative zones balanced with quiet areas</li>
      </ul>
      
      <h3>The Psychology of Color in Workspaces</h3>
      <p>We carefully selected a palette of warm neutrals punctuated by deep blues and greens. Research shows that blue enhances focus and mental clarity, while green promotes balance and reduces eye strain—perfect for a law office where attention to detail is paramount.</p>
      
      <h3>Measuring Success</h3>
      <p>Six months after completion, the STRATJURIS team reported a 25% increase in employee satisfaction scores and a noticeable improvement in client meetings and presentations. The space truly became a tool for their success.</p>
      
      <blockquote>
        "Good design is not just what looks good. It's what works well." - John Thackara
      </blockquote>
    `,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1200&q=80",
    category: "Commercial Design",
    author: "Emma Rodriguez",
    date: "2025-01-05",
    readTime: "6 min read",
    tags: ["commercial", "productivity", "workplace", "psychology"]
  },
  {
    title: "Color Psychology in Residential Interiors",
    summary: "Understanding how different colors affect mood and behavior, and how we use this knowledge to create homes that truly feel like sanctuaries.",
    content: `
      <h2>The Power of Color</h2>
      <p>Color is one of the most powerful tools in an interior designer's arsenal. It can make a small room feel spacious, a cold space feel warm, and dramatically impact the mood of everyone who enters. Understanding color psychology is essential to creating homes that not only look beautiful but feel right.</p>
      
      <h3>Warm vs. Cool: Setting the Tone</h3>
      <p>Warm colors (reds, oranges, yellows) create intimacy and energy, making them perfect for social spaces like living rooms and dining areas. Cool colors (blues, greens, purples) promote calm and concentration, ideal for bedrooms and home offices.</p>
      
      <h3>Our Color Philosophy</h3>
      <ul>
        <li><strong>Foundation Neutrals:</strong> Create a timeless base that won't date</li>
        <li><strong>Accent Colors:</strong> Inject personality and can be easily changed</li>
        <li><strong>Natural Elements:</strong> Wood tones and stone add warmth without overwhelming</li>
        <li><strong>Lighting Consideration:</strong> How colors change throughout the day</li>
      </ul>
      
      <blockquote>
        "Colors, like features, follow the changes of the emotions." - Pablo Picasso
      </blockquote>
      
      <h3>Case Study: A Family Home Transformation</h3>
      <p>In a recent residential project, we transformed a builder-grade home into a personalized family sanctuary. By introducing a sophisticated palette of sage greens, warm grays, and natural wood tones, we created spaces that feel both elegant and livable.</p>
      
      <p>The master bedroom features soft lavender accents known to promote relaxation and better sleep, while the children's playroom incorporates energizing but not overstimulating coral tones that encourage creativity without causing hyperactivity.</p>
    `,
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80",
    category: "Design Tips",
    author: "Lisa Thompson",
    date: "2024-12-28",
    readTime: "4 min read",
    tags: ["color", "psychology", "residential", "mood"]
  },
  {
    title: "The Future of Smart Home Integration",
    summary: "How we're seamlessly incorporating smart technology into our designs without compromising aesthetic appeal or user experience.",
    content: `
      <h2>Technology Meets Design</h2>
      <p>The smart home revolution is here, but too often technology feels like an afterthought—visible wires, clunky devices, and interfaces that clash with carefully curated interiors. Our approach is different: we integrate smart technology from the ground up, making it invisible yet infinitely useful.</p>
      
      <h3>Invisible Integration Principles</h3>
      <ul>
        <li>Hidden wiring and infrastructure planning from day one</li>
        <li>Smart switches and outlets that match the aesthetic</li>
        <li>Voice control systems with discrete microphone placement</li>
        <li>Automated lighting that responds to natural rhythms</li>
        <li>Climate control that learns and adapts</li>
      </ul>
      
      <h3>The Human Element</h3>
      <p>While technology can enhance our daily lives, it should never overwhelm or complicate them. We focus on solutions that genuinely improve quality of life—like automated blinds that adjust to the sun's position, or lighting systems that gradually dim in the evening to support natural sleep cycles.</p>
      
      <blockquote>
        "The best technology is invisible. It gets out of the way and lets you live, work, and play." - Jonathan Ive
      </blockquote>
      
      <h3>Looking Ahead</h3>
      <p>As we move forward, we're excited about developments in sustainable smart technology—solar integration, energy-efficient systems, and devices that help reduce rather than increase our environmental footprint.</p>
      
      <p>The goal is always the same: create homes that are more comfortable, efficient, and beautiful than ever before.</p>
    `,
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
    category: "Technology",
    author: "Michael Park",
    date: "2024-12-20",
    readTime: "5 min read",
    tags: ["smart home", "technology", "automation", "future"]
  },
  {
    title: "Lighting Design: Sculpting Spaces with Light",
    summary: "The art and science of lighting design, and how the right illumination can completely transform the feel and function of any space.",
    content: `
      <h2>Light as a Design Element</h2>
      <p>Lighting is often the last thing considered in a design project, but it should be one of the first. Good lighting design doesn't just illuminate—it sculpts space, creates mood, and can make or break the entire aesthetic of a room.</p>
      
      <h3>The Three Layers of Light</h3>
      <ul>
        <li><strong>Ambient Lighting:</strong> General illumination that fills the space</li>
        <li><strong>Task Lighting:</strong> Focused light for specific activities</li>
        <li><strong>Accent Lighting:</strong> Decorative lighting that adds drama and interest</li>
      </ul>
      
      <h3>Natural Light: The Foundation</h3>
      <p>Before adding any artificial lighting, we always start with natural light. How does sunlight move through the space? What are the views? How can we maximize daylight while controlling glare and heat?</p>
      
      <p>In our recent projects, we've used everything from strategically placed mirrors to light shelves and clerestory windows to bring natural light deeper into interior spaces.</p>
      
      <blockquote>
        "Architecture is a play of masses brought together in light." - Le Corbusier
      </blockquote>
      
      <h3>The Emotional Impact of Light</h3>
      <p>Different qualities of light evoke different emotions. Warm, dim lighting creates intimacy and relaxation—perfect for bedrooms and dining rooms. Cool, bright light promotes alertness and focus, ideal for kitchens and workspaces.</p>
      
      <p>We also consider the color temperature throughout the day, using smart lighting systems that can shift from energizing cool light in the morning to relaxing warm light in the evening.</p>
      
      <h3>Practical Tips</h3>
      <p>Always include dimmers—they're the most cost-effective way to dramatically increase lighting flexibility. And remember: you can never have too many light sources, but you can definitely have too few.</p>
    `,
    image: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80",
    category: "Design Tips",
    author: "Anna Kowalski",
    date: "2024-12-15",
    readTime: "6 min read",
    tags: ["lighting", "design", "ambiance", "natural light"]
  }
];

// Function to add sample blogs to Firebase
export const addSampleBlogs = async () => {
  try {
    console.log('Adding sample blogs to Firebase...');
    
    for (const blog of sampleBlogs) {
      const docRef = await addDoc(collection(db, 'blogs'), blog);
      console.log('Blog added with ID:', docRef.id);
    }
    
    console.log('All sample blogs added successfully!');
    return true;
  } catch (error) {
    console.error('Error adding sample blogs:', error);
    return false;
  }
};

// Function to be called from browser console
window.addSampleBlogs = addSampleBlogs;